import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  DayMealPlan,
  Meal,
  MealPlanData,
  Recipe,
  ApiIngredient,
  RecipeIngredient,
  isApiIngredient,
  LLMMealPlanResultOutgoing,
  LLMMealTimeSlotOutgoing,
  ConstraintsWithMacros,
} from "types/mealPlanning";
import { LLMMacros } from "types/common";

/**
 * Convert LLMMealPlanResultOutgoing to MealPlanData format
 * This handles the type differences between the API response format and the internal format
 */
export const convertLLMMealPlanToMealPlanData = (
  llmMealPlan: LLMMealPlanResultOutgoing
): MealPlanData => {
  // Convert constraints
  const constraints: ConstraintsWithMacros = {
    constraints: llmMealPlan.constraints.constraints,
    macros: llmMealPlan.constraints.macros as LLMMacros,
  };

  // Convert meal plan days
  const mealPlanDays: DayMealPlan[] = llmMealPlan.mealPlanDays.map((llmDay) => {
    const convertMealSlot = (slot: LLMMealTimeSlotOutgoing): Meal => {
      const recipes: Recipe[] = slot.recipes.map((llmRecipe) => {
        // Convert ingredients from OutgoingSearchResult to ApiIngredient
        const ingredients: ApiIngredient[] = llmRecipe.ingredients.map((searchResult) => {
          // Ensure nutritionPreview exists and has the required structure
          const nutritionPreview = searchResult.nutritionPreview || {
            portion: {
              quantity: 1,
              name: searchResult.portionSize || "serving",
              weight: {
                value: searchResult.weightGrams || 0,
                unit: "g",
              },
            },
            calories: 0,
            carbs: 0,
            fat: 0,
            protein: 0,
            fiber: 0,
          };

          const apiIngredient: ApiIngredient = {
            type: searchResult.type,
            displayName: searchResult.displayName,
            shortName: searchResult.shortName,
            longName: searchResult.longName,
            brandName: searchResult.brandName,
            nutritionPreview: {
              portion: nutritionPreview.portion || {
                quantity: 1,
                name: "serving",
                weight: { value: 0, unit: "g" },
              },
              calories: nutritionPreview.calories,
              carbs: nutritionPreview.carbs,
              fat: nutritionPreview.fat,
              protein: nutritionPreview.protein,
              fiber: nutritionPreview.fiber,
            },
          };

          return apiIngredient;
        });

        const recipe: Recipe = {
          name: llmRecipe.name,
          ingredients,
          preparation: llmRecipe.preparation,
          macros: llmRecipe.macros as LLMMacros,
        };

        return recipe;
      });

      return { recipes };
    };

    const dayMealPlan: DayMealPlan = {
      breakfast: convertMealSlot(llmDay.breakfast),
      lunch: convertMealSlot(llmDay.lunch),
      dinner: convertMealSlot(llmDay.dinner),
      snack: convertMealSlot(llmDay.snack),
      macros: llmDay.macros as LLMMacros,
    };

    return dayMealPlan;
  });

  return {
    constraints,
    mealPlanDays,
  };
};

  /**
   * Format an ingredient for display, handling both API and UI formats
   * Properly handles suggestedQuantity vs. regular quantity
   */
  export const formatIngredient = (
    ingredient: RecipeIngredient | ApiIngredient
  ): string => {
    if (!ingredient) {
      return "Unknown ingredient";
    }
  
    try {
      if (isApiIngredient(ingredient)) {
        // Convert API format to UI format on the fly
        const { displayName, nutritionPreview } = ingredient;
  
        if (!nutritionPreview || !nutritionPreview.portion) {
          return displayName || "Unknown ingredient";
        }
  
        const { portion } = nutritionPreview;
  
        // Handle quantity calculation
        // If suggestedQuantity exists, use it as the displayed quantity
        // Otherwise use the original portion quantity
        let displayQuantity = portion.quantity || 0;
  
        if (
          portion.suggestedQuantity &&
          Array.isArray(portion.suggestedQuantity) &&
          portion.suggestedQuantity.length >= 1
        ) {
          displayQuantity = portion.suggestedQuantity[0];
        }
  
        // Calculate actual weight based on quantity
        const weightValue = portion.weight?.value || 0;
        const actualWeight = weightValue * displayQuantity;
  
        // Format quantities to 1 decimal place if not a whole number
        const formattedQuantity = displayQuantity.toFixed(
          displayQuantity % 1 === 0 ? 0 : 1
        );
        const formattedWeight = actualWeight.toFixed(
          actualWeight % 1 === 0 ? 0 : 1
        );
  
        return `${displayName || "Unknown ingredient"} (${formattedQuantity} ${
          portion.name || "serving"
        }, ${formattedWeight}g)`;
      } else {
        // Use our UI format directly, but also format numbers
        if (!ingredient.ingredientName) {
          return "Unknown ingredient";
        }
  
        const portionQuantity = ingredient.portionQuantity || 0;
        const weightGrams = ingredient.weightGrams || 0;
  
        const formattedQuantity = portionQuantity.toFixed(
          portionQuantity % 1 === 0 ? 0 : 1
        );
        const formattedWeight = weightGrams.toFixed(
          weightGrams % 1 === 0 ? 0 : 1
        );
  
        return `${ingredient.ingredientName} (${formattedQuantity} ${
          ingredient.portionSize || "serving"
        }, ${formattedWeight}g)`;
      }
    } catch (error) {
      return "Error formatting ingredient";
    }
  };
  
/**
 * Format macros as a string for export
 */
const formatMacrosString = (
  calories: number,
  carbs: number,
  fat: number,
  protein: number,
  fiber = 0
): string => {
  return `Calories: ${Math.round(calories)} kcal | Carbs: ${Math.round(
    carbs
  )}g | Fat: ${Math.round(fat)}g | Protein: ${Math.round(
    protein
  )}g | Fiber: ${Math.round(fiber)}g`;
};

/**
 * Get nutrition values from a recipe
 */
const getRecipeNutrition = (recipe: Recipe) => {
  if (recipe.macros) {
    return {
      calories: recipe.macros.calories,
      carbs: recipe.macros.carbs,
      fat: recipe.macros.fat,
      protein: recipe.macros.protein,
      fiber: recipe.macros.fiber || 0,
    };
  }

  if (recipe.totalNutrition) {
    return {
      calories: recipe.totalNutrition.calories,
      carbs: recipe.totalNutrition.carbs,
      fat: recipe.totalNutrition.fat,
      protein: recipe.totalNutrition.protein,
      fiber: recipe.totalNutrition.fiber || 0,
    };
  }

  // Default values if nothing is available
  return { calories: 0, carbs: 0, fat: 0, protein: 0, fiber: 0 };
};

/**
 * Get nutrition values for a meal
 */
const getMealNutrition = (meal: Meal) => {
  if (meal.totalNutrition) {
    return {
      ...meal.totalNutrition,
      fiber: meal.totalNutrition.fiber || 0,
    };
  }

  // Calculate from recipes
  const totals = { calories: 0, carbs: 0, fat: 0, protein: 0, fiber: 0 };
  meal.recipes.forEach((recipe) => {
    const nutrition = getRecipeNutrition(recipe);
    totals.calories += nutrition.calories;
    totals.carbs += nutrition.carbs;
    totals.fat += nutrition.fat;
    totals.protein += nutrition.protein;
    totals.fiber += nutrition.fiber || 0;
  });

  return totals;
};

/**
 * Get nutrition values from a day meal plan
 */
const getDayNutrition = (day: DayMealPlan) => {
  // First try to use macros if available
  if (day.macros) {
    return {
      calories: day.macros.calories,
      carbs: day.macros.carbs,
      fat: day.macros.fat,
      protein: day.macros.protein,
      fiber: day.macros.fiber || 0,
    };
  }

  // Then try totalNutrition if available
  if (day.totalNutrition) {
    return {
      ...day.totalNutrition,
      fiber: day.totalNutrition.fiber || 0,
    };
  }

  // Calculate from meals if needed
  const totals = { calories: 0, carbs: 0, fat: 0, protein: 0, fiber: 0 };
  ["breakfast", "lunch", "dinner", "snack"].forEach((mealType) => {
    if (day[mealType as keyof typeof day]) {
      const meal = day[mealType as keyof typeof day] as Meal;
      const nutrition = getMealNutrition(meal);
      totals.calories += nutrition.calories || 0;
      totals.protein += nutrition.protein || 0;
      totals.carbs += nutrition.carbs || 0;
      totals.fat += nutrition.fat || 0;
      totals.fiber += nutrition.fiber || 0;
    }
  });

  return totals;
};

/**
 * Export meal plan data to CSV format
 * @param mealPlanData The meal plan data to export (can be either MealPlanData or LLMMealPlanResultOutgoing)
 * @returns A promise that resolves when the export is complete
 */
export const exportToCSV = (mealPlanData: MealPlanData | LLMMealPlanResultOutgoing): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      // Convert to MealPlanData format if needed
      const data: MealPlanData = 'shoppingList' in mealPlanData 
        ? convertLLMMealPlanToMealPlanData(mealPlanData)
        : mealPlanData;

      // More detailed CSV with ingredients broken out into their own rows
      let csvContent =
        "Day,Meal,Item Type,Name,Serving Size,Weight (g),Calories,Protein (g),Carbs (g),Fat (g),Fiber (g)\n";

      data.mealPlanDays.forEach((day, dayIndex) => {
        const dayNumber = dayIndex + 1;
        const dayNutrition = getDayNutrition(day);

        // For each meal type (breakfast, lunch, dinner, snack)
        ["breakfast", "lunch", "dinner", "snack"].forEach((mealType) => {
          const meal = day[mealType as keyof DayMealPlan] as Meal;
          const mealNutrition = getMealNutrition(meal);
          const capitalizedMealType =
            mealType.charAt(0).toUpperCase() + mealType.slice(1);

          // Skip the meal total line if there's only one recipe (redundant)
          const shouldShowMealTotal = meal.recipes.length > 1;

          if (shouldShowMealTotal) {
            // Add a row for the meal summary
            csvContent += `${dayNumber},${capitalizedMealType},Meal Total,"","",""`;
            csvContent += `,${Math.round(mealNutrition.calories)}`;
            csvContent += `,${Math.round(mealNutrition.protein)}`;
            csvContent += `,${Math.round(mealNutrition.carbs)}`;
            csvContent += `,${Math.round(mealNutrition.fat)}`;
            csvContent += `,${Math.round(mealNutrition.fiber)}\n`;
          }

          // For each recipe in the meal
          meal.recipes.forEach((recipe) => {
            const recipeNutrition = getRecipeNutrition(recipe);

            // Add a row for the recipe summary
            csvContent += `${dayNumber},${capitalizedMealType},Recipe,"${recipe.name}","",""`;
            csvContent += `,${Math.round(recipeNutrition.calories)}`;
            csvContent += `,${Math.round(recipeNutrition.protein)}`;
            csvContent += `,${Math.round(recipeNutrition.carbs)}`;
            csvContent += `,${Math.round(recipeNutrition.fat)}`;
            csvContent += `,${Math.round(recipeNutrition.fiber)}\n`;

            // Add a row for each ingredient
            recipe.ingredients.forEach((ingredient) => {
              let ingredientName = "";
              let servingSize = "";
              let weightGrams = 0;
              let calories = 0;
              let protein = 0;
              let carbs = 0;
              let fat = 0;
              let fiber = 0;

              if (isApiIngredient(ingredient)) {
                const { displayName, nutritionPreview } = ingredient;
                const { portion } = nutritionPreview;

                // Handle quantity calculation - use suggestedQuantity if available
                let displayQuantity = portion.quantity || 0;
                if (
                  portion.suggestedQuantity &&
                  Array.isArray(portion.suggestedQuantity) &&
                  portion.suggestedQuantity.length >= 1
                ) {
                  displayQuantity = portion.suggestedQuantity[0];
                }

                // Calculate actual weight based on display quantity
                const weightValue = portion.weight?.value || 0;
                const actualWeight = weightValue * displayQuantity;

                // Format quantities to 1 decimal place if not a whole number
                const formattedQuantity = displayQuantity.toFixed(
                  displayQuantity % 1 === 0 ? 0 : 1
                );
                const formattedWeight = actualWeight.toFixed(
                  actualWeight % 1 === 0 ? 0 : 1
                );

                ingredientName = displayName || "Unknown ingredient";
                servingSize = `${formattedQuantity} ${
                  portion.name || "serving"
                }`;
                weightGrams = parseFloat(formattedWeight);

                // Extract nutrition data
                calories = nutritionPreview.calories || 0;
                protein = nutritionPreview.protein || 0;
                carbs = nutritionPreview.carbs || 0;
                fat = nutritionPreview.fat || 0;
                fiber = nutritionPreview.fiber || 0;
              } else {
                // Using UI format
                ingredientName =
                  ingredient.ingredientName || "Unknown ingredient";
                servingSize = `${ingredient.portionQuantity.toFixed(
                  ingredient.portionQuantity % 1 === 0 ? 0 : 1
                )} ${ingredient.portionSize || "serving"}`;
                weightGrams = ingredient.weightGrams;

                // Extract nutrition data
                calories = ingredient.calories || 0;
                protein = ingredient.protein || 0;
                carbs = ingredient.carbs || 0;
                fat = ingredient.fat || 0;
                fiber = ingredient.fiber || 0;
              }

              // Add the ingredient row
              csvContent += `${dayNumber},${capitalizedMealType},Ingredient,"${ingredientName}","${servingSize}",${weightGrams.toFixed(
                1
              )}`;
              csvContent += `,${Math.round(calories)}`;
              csvContent += `,${protein.toFixed(1)}`;
              csvContent += `,${carbs.toFixed(1)}`;
              csvContent += `,${fat.toFixed(1)}`;
              csvContent += `,${fiber.toFixed(1)}\n`;
            });
          });
        });

        // Add a row for the day summary
        csvContent += `${dayNumber},All Meals,Day Total,"","",""`;
        csvContent += `,${Math.round(dayNutrition.calories)}`;
        csvContent += `,${Math.round(dayNutrition.protein)}`;
        csvContent += `,${Math.round(dayNutrition.carbs)}`;
        csvContent += `,${Math.round(dayNutrition.fat)}`;
        csvContent += `,${Math.round(dayNutrition.fiber)}\n`;

        // Add a blank row between days
        csvContent += "\n";
      });

      const encodedUri =
        "data:text/csv;charset=utf-8," + encodeURIComponent(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute(
        "download",
        `meal_plan_${new Date().toISOString().slice(0, 10)}.csv`
      );
      link.click();

      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Export meal plan data to PDF format
 * @param mealPlanData The meal plan data to export (can be either MealPlanData or LLMMealPlanResultOutgoing)
 * @returns A promise that resolves when the export is complete
 */
export const exportToPDF = (mealPlanData: MealPlanData | LLMMealPlanResultOutgoing): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      // Convert to MealPlanData format if needed
      const data: MealPlanData = 'shoppingList' in mealPlanData 
        ? convertLLMMealPlanToMealPlanData(mealPlanData)
        : mealPlanData;

      const pdf = new jsPDF();

      data.mealPlanDays.forEach((day, dayIndex) => {
        // Add a new page for each day except the first one
        if (dayIndex > 0) {
          pdf.addPage();
        }

        // Day header
        pdf.setFontSize(18);
        pdf.text(`Meal Plan - Day ${dayIndex + 1}`, 14, 20);

        // Day nutrition summary - use the getDayNutrition helper
        const dayNutrition = getDayNutrition(day);
        pdf.setFontSize(12);
        pdf.text(
          `Daily Nutrition: ${formatMacrosString(
            dayNutrition.calories,
            dayNutrition.carbs,
            dayNutrition.fat,
            dayNutrition.protein,
            dayNutrition.fiber
          )}`,
          14,
          30
        );

        let yPos = 40;

        // Each meal
        ["breakfast", "lunch", "dinner", "snack"].forEach((mealType) => {
          const meal = day[mealType as keyof DayMealPlan] as Meal;
          const mealTypeName =
            mealType.charAt(0).toUpperCase() + mealType.slice(1);

          // Meal header
          pdf.setFontSize(14);
          pdf.text(`${mealTypeName}`, 14, yPos);
          yPos += 7;

          // Meal nutrition
          pdf.setFontSize(10);
          pdf.text(
            `${formatMacrosString(
              getMealNutrition(meal).calories,
              getMealNutrition(meal).carbs,
              getMealNutrition(meal).fat,
              getMealNutrition(meal).protein,
              getMealNutrition(meal).fiber
            )}`,
            14,
            yPos
          );
          yPos += 7;

          // Each recipe
          meal.recipes.forEach((recipe) => {
            // Check if we need a new page
            if (yPos > 270) {
              pdf.addPage();
              yPos = 20;
            }

            // Recipe name
            pdf.setFontSize(12);
            pdf.text(`${recipe.name}`, 18, yPos);
            yPos += 6;

            // Recipe nutrition
            const recipeNutrition = getRecipeNutrition(recipe);
            pdf.setFontSize(10);
            pdf.text(
              `${formatMacrosString(
                recipeNutrition.calories,
                recipeNutrition.carbs,
                recipeNutrition.fat,
                recipeNutrition.protein,
                recipeNutrition.fiber
              )}`,
              22,
              yPos
            );
            yPos += 6;

            // Ingredients
            pdf.setFontSize(10);
            pdf.text("Ingredients:", 22, yPos);
            yPos += 5;

            recipe.ingredients.forEach((ingredient) => {
              // Check if we need a new page
              if (yPos > 270) {
                pdf.addPage();
                yPos = 20;
              }

              pdf.text(`- ${formatIngredient(ingredient)}`, 26, yPos);
              yPos += 5;
            });

            // Preparation
            if (recipe.preparation) {
              // Check if we need a new page
              if (yPos > 270) {
                pdf.addPage();
                yPos = 20;
              }

              pdf.text("Preparation:", 22, yPos);
              yPos += 5;

              // Split preparation into lines to avoid text overflow
              const preparationLines = pdf.splitTextToSize(
                recipe.preparation,
                170
              );
              preparationLines.forEach((line: string) => {
                // Check if we need a new page
                if (yPos > 270) {
                  pdf.addPage();
                  yPos = 20;
                }

                pdf.text(line, 26, yPos);
                yPos += 5;
              });
            }

            yPos += 5; // Space between recipes
          });

          yPos += 5; // Space between meals
        });
      });

      pdf.save(`meal_plan_${new Date().toISOString().slice(0, 10)}.pdf`);
      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Create a more formatted PDF with tables and styling
 * @param mealPlanData The meal plan data to export (can be either MealPlanData or LLMMealPlanResultOutgoing)
 * @returns A promise that resolves when the export is complete
 */
export const exportToStyledPDF = (
  mealPlanData: MealPlanData | LLMMealPlanResultOutgoing
): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      // Convert to MealPlanData format if needed
      const data: MealPlanData = 'shoppingList' in mealPlanData 
        ? convertLLMMealPlanToMealPlanData(mealPlanData)
        : mealPlanData;

      const pdf = new jsPDF();

      data.mealPlanDays.forEach((day, dayIndex) => {
        // Add a new page for each day except the first one
        if (dayIndex > 0) {
          pdf.addPage();
        }

        // Add header
        pdf.setFillColor(41, 128, 185); // Blue header
        pdf.rect(0, 0, 210, 20, "F");
        pdf.setTextColor(255, 255, 255); // White text
        pdf.setFontSize(16);
        pdf.text(`Meal Plan - Day ${dayIndex + 1}`, 105, 12, {
          align: "center",
        });

        // Reset text color for the rest of the page
        pdf.setTextColor(0, 0, 0);

        // Day nutrition summary in a box
        pdf.setFillColor(240, 240, 240); // Light gray background
        pdf.rect(10, 25, 190, 15, "F");
        pdf.setFontSize(11);

        const dayNutrition = getDayNutrition(day);

        pdf.text(
          `Daily Nutrition Target: ${formatMacrosString(
            dayNutrition.calories,
            dayNutrition.carbs,
            dayNutrition.fat,
            dayNutrition.protein,
            dayNutrition.fiber
          )}`,
          105,
          33,
          { align: "center" }
        );

        let yPos = 50;

        // Each meal
        ["breakfast", "lunch", "dinner", "snack"].forEach((mealType) => {
          const meal = day[mealType as keyof DayMealPlan] as Meal;
          const mealTypeName =
            mealType.charAt(0).toUpperCase() + mealType.slice(1);

          // Process each recipe within the meal
          meal.recipes.forEach((recipe) => {
            // Check if we need a new page
            if (yPos > 250) {
              pdf.addPage();
              yPos = 20;
            }

            // Add meal + recipe header with background (blue header)
            pdf.setFillColor(52, 152, 219); // Lighter blue for meal header
            pdf.rect(10, yPos - 5, 190, 10, "F");
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(12);
            pdf.text(`${mealTypeName} - ${recipe.name}`, 15, yPos);

            pdf.setTextColor(0, 0, 0); // Reset text color
            yPos += 10;

            // Recipe nutrition with background (gray bar)
            pdf.setFillColor(230, 230, 230);
            pdf.rect(15, yPos - 5, 180, 10, "F");

            // Get recipe nutrition
            const recipeNutrition = getRecipeNutrition(recipe);
            pdf.setFontSize(9);

            // Center the nutrition info in the gray bar
            pdf.text(
              `${formatMacrosString(
                recipeNutrition.calories,
                recipeNutrition.carbs,
                recipeNutrition.fat,
                recipeNutrition.protein,
                recipeNutrition.fiber
              )}`,
              105, // Center aligned
              yPos,
              { align: "center" }
            );

            yPos += 8;

            // Ingredients table
            const ingredientRows = recipe.ingredients.map((ingredient) => [
              formatIngredient(ingredient),
            ]);

            // Use autoTable directly as a function instead of as a method on pdf
            autoTable(pdf, {
              startY: yPos,
              head: [["Ingredients"]],
              body: ingredientRows,
              theme: "grid",
              headStyles: { fillColor: [200, 200, 200], textColor: [0, 0, 0] },
              margin: { left: 20, right: 20 },
              tableWidth: 170,
            });

            // Get the last table's final position
            yPos = (pdf as any).lastAutoTable.finalY + 5;

            // Preparation
            if (recipe.preparation) {
              // Use autoTable directly as a function
              autoTable(pdf, {
                startY: yPos,
                head: [["Preparation"]],
                body: [[recipe.preparation]],
                theme: "grid",
                headStyles: {
                  fillColor: [200, 200, 200],
                  textColor: [0, 0, 0],
                },
                margin: { left: 20, right: 20 },
                tableWidth: 170,
              });

              // Get the last table's final position
              yPos = (pdf as any).lastAutoTable.finalY + 10;
            } else {
              yPos += 10;
            }
          });
        });
      });

      pdf.save(`meal_plan_styled_${new Date().toISOString().slice(0, 10)}.pdf`);
      resolve();
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Export meal plan raw data to JSON
 * @param mealPlanData The meal plan data to export (can be either MealPlanData or LLMMealPlanResultOutgoing)
 * @returns A promise that resolves when the export is complete
 */
export const exportToJSON = (mealPlanData: MealPlanData | LLMMealPlanResultOutgoing): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      // For JSON export, we'll keep the original format without conversion
      const dataStr = JSON.stringify(mealPlanData, null, 2);
      const dataUri =
        "data:application/json;charset=utf-8," + encodeURIComponent(dataStr);

      const exportName = `meal_plan_${new Date()
        .toISOString()
        .slice(0, 10)}.json`;
      const linkElement = document.createElement("a");
      linkElement.setAttribute("href", dataUri);
      linkElement.setAttribute("download", exportName);
      linkElement.click();

      resolve();
    } catch (error) {
      reject(error);
    }
  });
};
